import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import axios from "axios";
import { FADE_IN, SLIDE_UP } from "../lib/animations";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDiscordCallback = useCallback(async (code) => {
    setLoading(true);
    setError("");
    try {
      const redirectUri = window.location.origin;
      const res = await axios.post(`${API}/auth/discord/dashboard-callback`, {
        code,
        redirect_uri: redirectUri,
      });

      sessionStorage.setItem("authenticated", "true");
      sessionStorage.setItem("currentUser", res.data.display_name);
      sessionStorage.setItem("isAdmin", res.data.is_admin ? "true" : "false");
      sessionStorage.setItem("userRole", res.data.role || "user");
      sessionStorage.setItem("canViewReports", res.data.can_view_reports ? "true" : "false");
      sessionStorage.setItem("discordId", res.data.discord_id || "");
      if (res.data.token) {
        sessionStorage.setItem("apiToken", res.data.token);
      }

      window.history.replaceState({}, document.title, "/");
      onSuccess(
        res.data.display_name,
        res.data.is_admin,
        res.data.role,
        res.data.can_view_reports,
        res.data.permissions,
        res.data.discord_id
      );
    } catch (err) {
      const detail = err.response?.data?.detail || "Authentication failed";
      setError(detail);
      window.history.replaceState({}, document.title, "/");
    } finally {
      setLoading(false);
    }
  }, [onSuccess]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    if (code) {
      handleDiscordCallback(code);
    }
  }, [handleDiscordCallback]);

  const handleDiscordLogin = async () => {
    setLoading(true);
    try {
      const redirectUri = window.location.origin;
      const res = await axios.get(`${API}/auth/discord/url`, {
        params: { redirect_uri: redirectUri },
      });
      window.location.href = res.data.url;
    } catch {
      setError("Failed to start Discord login");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] tactical-grid flex items-center justify-center p-4" data-testid="login-page">
      <div className="noise-overlay" />
      <motion.div
        {...SLIDE_UP}
        transition={{ duration: 0.3 }}
        className="w-full max-w-sm"
      >
        <div className="glass-overlay p-8 relative">
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5865F2] to-transparent" />

          <div className="flex flex-col items-center">
            {/* Logo */}
            <img src="/logo.png" alt="Union Interactive" className="w-16 h-16 object-contain mb-5" />

            {/* Terminal text */}
            <p className="font-mono text-[10px] text-zinc-600 tracking-[0.3em] mb-1">// IDENTITY VERIFICATION</p>
            <h1 className="font-mono text-lg font-bold tracking-wider mb-1">
              <span className="text-white">UNION </span><span className="text-[#FFD600]">INTERACTIVE</span>
            </h1>
            <p className="font-mono text-[11px] text-zinc-500 tracking-widest mb-8">STAFF MANAGEMENT TERMINAL</p>

            {error && (
              <motion.div
                {...FADE_IN}
                className="w-full flex items-center gap-2 px-3 py-2 bg-[#FF3366]/10 border border-[#FF3366]/30 mb-4"
                data-testid="login-error"
              >
                <Lock className="w-3 h-3 text-[#FF3366]" />
                <span className="font-mono text-xs text-[#FF3366]">{error}</span>
              </motion.div>
            )}

            {loading ? (
              <div className="flex items-center justify-center gap-3 py-4">
                <div className="w-4 h-4 border-2 border-[#5865F2]/30 border-t-[#5865F2] rounded-full animate-spin" />
                <span className="font-mono text-xs text-zinc-400">AUTHENTICATING...</span>
              </div>
            ) : (
              <button
                onClick={handleDiscordLogin}
                className="w-full py-3 px-6 bg-[#5865F2] hover:bg-[#4752C4] text-white font-mono font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-3 transition-colors"
                data-testid="discord-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286z" />
                </svg>
                LOGIN WITH DISCORD
              </button>
            )}

            <p className="font-mono text-[9px] text-zinc-700 mt-6 tracking-wider">AUTHORIZED PERSONNEL ONLY</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
